import React from 'react';
import { Layers } from 'lucide-react';

interface EmptyCategoryGuideProps {
  type: 'live' | 'movie' | 'series';
  categoryName: string;
  availableCategories: string[];
  onSelectCategory: (category: string) => void;
  onOpenMobileDrawer?: () => void;
  categoryCounts?: Record<string, number>;
  totalItemsCount?: number;
}

/**
 * Remove prefixos redundantes como "Filmes |", "Canais |", "Séries |", "VOD |", etc.
 * Ex: "Filmes | Romance" => "Romance"
 * Ex: "CANAIS | TELECINE" => "TELECINE"
 * Ex: "Séries - Animes" => "Animes"
 */
export const cleanCategoryName = (name: string): string => {
  if (!name) return '';
  const prefixRegex = /^(?:\[?(?:canais|canal|filmes|filme|séries|series|vod)\]?)\s*[\/\|\:\-\]]\s*/i;
  const cleaned = name.replace(prefixRegex, '').trim();
  return cleaned.length > 0 ? cleaned : name.trim();
};

export const EmptyCategoryGuide: React.FC<EmptyCategoryGuideProps> = ({
  categoryName,
  availableCategories,
  onSelectCategory,
  categoryCounts,
}) => {
  // Lista todas as categorias reais disponíveis com conteúdo, ignorando a categoria vazia atual
  const displayCategories = availableCategories.filter((cat) => {
    if (cat === categoryName) return false;
    if (cat === 'Favoritos' || cat === 'Continuar Assistindo') return false;
    if (categoryCounts && typeof categoryCounts[cat] === 'number' && categoryCounts[cat] <= 0) return false;
    return true;
  });

  return (
    <div className="w-full max-w-5xl mx-auto py-6 sm:py-10 px-2 sm:px-6 select-none animate-in fade-in duration-200">
      {/* Título Direto e Limpo */}
      <div className="text-center mb-5 sm:mb-6">
        <h2 className="inline-flex items-center gap-2 text-xs sm:text-sm font-black text-amber-400 tracking-wider uppercase px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20 shadow-sm">
          <Layers className="w-4 h-4 text-amber-400" />
          <span>ESCOLHA UMA CATEGORIA</span>
        </h2>
      </div>

      {/* Tags Pequenas e Clicáveis com todas as categorias */}
      <div className="flex flex-wrap items-center justify-center gap-1.5 sm:gap-2 max-h-[70vh] overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-zinc-800">
        {displayCategories.map((cat) => {
          const cleanedName = cleanCategoryName(cat);
          const count = categoryCounts ? categoryCounts[cat] : undefined;

          return (
            <button
              key={cat}
              type="button"
              onClick={() => onSelectCategory(cat)}
              className="px-2.5 py-1 sm:px-3 sm:py-1.5 rounded-lg bg-zinc-900/90 hover:bg-amber-500 hover:text-black border border-zinc-800 hover:border-amber-400 text-zinc-300 text-[11px] sm:text-xs font-semibold transition active:scale-95 cursor-pointer flex items-center shadow-sm group"
              title={`Abrir categoria: ${cat}`}
            >
              <span className="truncate max-w-[140px] sm:max-w-[200px]">
                {cleanedName}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
